import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaVideo, FaTimes, FaFilm } from 'react-icons/fa';
import { getToken } from '../utils/auth';
import { getApiBaseUrl } from '../utils/api';

const LiveStreamBanner: React.FC = () => {
    const [stream, setStream] = useState<any>(null);
    const [isVisible, setIsVisible] = useState(true);
    const navigate = useNavigate();

    const location = useLocation();

    useEffect(() => {
        checkStream();
        // Check every 30 seconds for new stream
        const interval = setInterval(checkStream, 30000);
        return () => clearInterval(interval);
    }, []);

    // Return null immediately for hidden paths
    const hiddenPaths = ['/welcome', '/maintenance', '/login', '/register', '/forgot-password'];
    if (hiddenPaths.some(path => location.pathname.startsWith(path))) {
        return null;
    }

    const checkStream = async () => {
        try {
            const token = getToken();
            if (!token) return;

            const API_URL = getApiBaseUrl();

            // Add timeout to prevent hanging requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

            const response = await fetch(`${API_URL}/stream/current`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                // Silently fail if endpoint doesn't exist
                setStream(null);
                localStorage.removeItem('is_live_stream_active');
                window.dispatchEvent(new CustomEvent('livestream:status', { detail: { active: false } }));
                return;
            }

            const data = await response.json();
            if (data.stream && data.stream.notify) {
                setStream(data.stream);
                setIsVisible(true);
                // Notify other components (like AnnouncementBanner) to hide
                localStorage.setItem('is_live_stream_active', 'true');
                window.dispatchEvent(new CustomEvent('livestream:status', { detail: { active: true } }));
            } else {
                setStream(null);
                localStorage.removeItem('is_live_stream_active');
                window.dispatchEvent(new CustomEvent('livestream:status', { detail: { active: false } }));
            }
        } catch (error) {
            // Silently handle errors (endpoint may not exist or network timeout)
            setStream(null);
            localStorage.removeItem('is_live_stream_active');
            window.dispatchEvent(new CustomEvent('livestream:status', { detail: { active: false } }));
        }
    };

    const handleClick = () => {
        navigate('/live');
    };

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsVisible(false);
        localStorage.removeItem('is_live_stream_active');
        window.dispatchEvent(new CustomEvent('livestream:status', { detail: { active: false } }));
    };

    if (!stream || !isVisible) return null;

    const isMovie = stream.type === 'movie';
    const color = stream.color || 'red';

    const getColorClasses = (c: string) => {
        switch (c) {
            case 'blue': return 'from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600';
            case 'green': return 'from-green-600 to-green-500 hover:from-green-700 hover:to-green-600';
            case 'purple': return 'from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600';
            case 'orange': return 'from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600';
            case 'black': return 'from-gray-900 to-gray-800 hover:from-black hover:to-gray-900';
            case 'pink': return 'from-pink-600 to-pink-500 hover:from-pink-700 hover:to-pink-600';
            case 'indigo': return 'from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600';
            case 'teal': return 'from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600';
            case 'cyan': return 'from-cyan-600 to-cyan-500 hover:from-cyan-700 hover:to-cyan-600';
            case 'red':
            default: return 'from-red-600 to-red-500 hover:from-red-700 hover:to-red-600';
        }
    };

    const getBadgeTextColorClass = (c: string) => {
        switch (c) {
            case 'blue': return 'text-blue-600';
            case 'green': return 'text-green-600';
            case 'purple': return 'text-purple-600';
            case 'orange': return 'text-orange-600';
            case 'black': return 'text-gray-900';
            case 'pink': return 'text-pink-600';
            case 'indigo': return 'text-indigo-600';
            case 'teal': return 'text-teal-600';
            case 'cyan': return 'text-cyan-600';
            case 'red':
            default: return 'text-red-600';
        }
    };

    const colorClasses = getColorClasses(color);
    const badgeTextColor = getBadgeTextColorClass(color);

    return (
        <div
            onClick={handleClick}
            className={`relative bg-gradient-to-r ${colorClasses} text-white shadow-sm z-30 transition-all duration-200 cursor-pointer group`}
        >
            <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-4 py-2 px-4 sm:px-6 relative z-10">
                {/* Icon Label */}
                <div className="hidden sm:flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full border border-white/30 backdrop-blur-sm">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-white">
                        {isMovie ? 'PHIM' : 'TRỰC TIẾP'}
                    </span>
                </div>

                {/* Mobile Icon */}
                <div className="sm:hidden flex-shrink-0 text-white">
                    {isMovie ? (
                        <FaFilm className="w-5 h-5 animate-pulse" />
                    ) : (
                        <FaVideo className="w-5 h-5 animate-pulse" />
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden">
                    <div className="flex items-center justify-center sm:justify-start gap-2">
                        <span className="font-medium text-white text-sm sm:text-base tracking-wide truncate flex items-center">
                            <span className={`font-bold bg-white ${badgeTextColor} text-xs px-1.5 py-0.5 rounded mr-2 uppercase block`}>
                                {isMovie ? 'PHIM' : 'TRỰC TIẾP'}
                            </span>
                            {stream.title}
                        </span>
                        <span className="hidden sm:inline-block text-xs text-white/80">• Nhấn để xem ngay</span>
                    </div>
                </div>

                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="flex-shrink-0 p-1.5 hover:bg-white/20 rounded-full transition-all duration-200 text-white/80 hover:text-white"
                    title="Đóng thông báo"
                    style={{ minWidth: '32px', minHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <FaTimes className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default LiveStreamBanner;
